import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, LayoutGrid } from "lucide-react";
import Container from "./Container";
import MultiCrousel from "./MultiCrousel";
import httpCommon from "../http-common";

function Hero() {
  const [properties, setProperties] = useState([]);
  const [propertyCounts, setPropertyCounts] = useState({
    Bengaluru: 0,
    Hyderabad: 0,
    Tirupati: 0,
  });
  const [featuredPropertyCounts, setFeaturedPropertyCounts] = useState({
    Bengaluru: 0,
    Hyderabad: 0,
    Tirupati: 0,
  });

  // Fetch properties data
  const getPropertiesData = async () => {
    try {
      const response = await httpCommon.get("/list");
    //  console.log("response____",response);
      if (response.data.status === "success") {
        const properties = response.data.data;
        setProperties(properties);
        updatePropertyCounts(properties);
        updateFeaturedPropertyCounts(properties);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const updatePropertyCounts = (properties) => {
    const counts = {
      Bengaluru: 0,
      Hyderabad: 0,
      Tirupati: 0,
    };

    properties.forEach((property) => {
      if (property.ltg_regions === "Bengaluru") counts.Bengaluru += 1;
      if (property.ltg_regions === "Hyderabad") counts.Hyderabad += 1;
      if (property.ltg_regions === "Tirupati") counts.Tirupati += 1;
    });

    setPropertyCounts(counts);
  };

  const updateFeaturedPropertyCounts = (properties) => {
    const counts = {
      Bengaluru: 0,
      Hyderabad: 0,
      Tirupati: 0,
    };

    const featuredProperties = properties?.filter(
      (property) =>
        property.ltg_mark_as_featured === "true" &&
        ["Bengaluru", "Hyderabad", "Tirupati"].includes(property.ltg_regions)
    );

    featuredProperties.forEach((property) => {
      if (property.ltg_regions === "Bengaluru") counts.Bengaluru += 1;
      if (property.ltg_regions === "Hyderabad") counts.Hyderabad += 1;
      if (property.ltg_regions === "Tirupati") counts.Tirupati += 1;
    });

    setFeaturedPropertyCounts(counts);
  };

  function formatPrice(price, onlyFormatted = false) {
    if (price == null) {
      return onlyFormatted ? "N/A" : { numeric: 0, formatted: "N/A" };
    }

    const numericPrice = parseFloat(price.replace(/,/g, ""));
    if (isNaN(numericPrice)) {
      return onlyFormatted ? "N/A" : { numeric: 0, formatted: "N/A" };
    }

    let formatted;
    if (numericPrice < 1000) {
      formatted = numericPrice.toString();
    } else if (numericPrice < 100000) {
      formatted = `${(numericPrice / 1000).toFixed(0)} Thousand`;
    } else if (numericPrice < 10000000) {
      const lakhs = numericPrice / 100000;
      formatted = lakhs % 1 === 0 ? `${lakhs.toFixed(0)} Lakhs` : `${lakhs.toFixed(1)} Lakhs`;
    } else {
      const crores = numericPrice / 10000000;
      formatted = crores % 1 === 0 ? `${crores.toFixed(0)} Cr` : `${crores.toFixed(2)} Cr`;
    }

    return onlyFormatted ? formatted : { numeric: numericPrice, formatted };
  }


  const getPropertyDetails = (type, item) => {
    switch (type) {
      case 'CommercialProperties':
        return {
          description: item.ltg_det_comm_prop_desc,
          price: formatPrice(item.ltg_det_comm_prop_sale_price, true),
          suffixPrice: formatPrice(item.ltg_det_comm_prop_suffix_price, true),
          callForPrice: item?.ltg_det_comm_prop_call_for_price,
          bedroom: '', // empty for CommercialProperties
          bathroom: '', // empty for CommercialProperties
          parking: item.ltg_det_comm_prop_pmts_car_parking,
          area: item.ltg_det_comm_prop_pmts_area_dts,
          address: item.ltg_det_comm_prop_address,
          RowID: item.ltg_det_mstRowID,
          type: item.ltg_type,
          featured: item.ltg_mark_as_featured,
        };
      case 'PentHouses':
        return {
          description: item.ltg_det_penthouses_desc,
          price: formatPrice(item.ltg_det_penthouses_sale_price, true),
          suffixPrice: formatPrice(item.ltg_det_penthouses_suffix_price, true),
          callForPrice: item?.ltg_det_penthouses_call_for_price,
          bedroom: item.ltg_det_penthouses_pmts_bed_rooms,
          bathroom: item.ltg_det_penthouses_pmts_bath_rooms,
          parking: item.ltg_det_penthouses_pmts_car_parking,
          area: item.ltg_det_penthouses_pmts_area_dts,
          address: item.ltg_det_penthouses_address,
          RowID: item.ltg_det_mstRowID,
          type: item.ltg_type,
          featured: item.ltg_mark_as_featured,
        };
      case 'Plots':
        return {
          description: item.ltg_det_plot_desc,
          price: formatPrice(item.ltg_det_plot_sale_price, true),
          suffixPrice: formatPrice(item.ltg_det_plot_suffix_price, true),
          callForPrice: item?.ltg_det_plot_call_for_price,
          bedroom: '', // empty for Plots
          bathroom: '', // empty for Plots
          parking: '', // empty for Plots
          area: item.ltg_det_plot_pmts_area_dts,
          address: item.ltg_det_plot_address,
          RowID: item.ltg_det_mstRowID,
          type: item.ltg_type,
          featured: item.ltg_mark_as_featured,
        };
      case 'RowHouses':
        return {
          description: item.ltg_det_row_house_desc,
          price: formatPrice(item.ltg_det_row_house_sale_price, true),
          suffixPrice: formatPrice(item.ltg_det_row_house_suffix_price, true),
          callForPrice: item?.ltg_det_row_house_call_for_price,
          bedroom: item.ltg_det_row_house_pmts_bed_rooms,
          bathroom: item.ltg_det_row_house_pmts_bath_rooms,
          parking: item.ltg_det_row_house_pmts_car_parking,
          area: item.ltg_det_row_house_pmts_area_dts,
          address: item.ltg_det_row_house_address,
          RowID: item.ltg_det_mstRowID,
          type: item.ltg_type,
          featured: item.ltg_mark_as_featured,
        };
      case 'Villaments':
        return {
          description: item.ltg_det_villaments_desc,
          price: formatPrice(item.ltg_det_villaments_sale_price, true),
          suffixPrice: formatPrice(item.ltg_det_villaments_suffix_price, true),
          callForPrice: item?.ltg_det_villaments_call_for_price,
          bedroom: item.ltg_det_villaments_pmts_bed_rooms,
          bathroom: item.ltg_det_villaments_pmts_bath_rooms,
          parking: item.ltg_det_villaments_pmts_car_parking,
          area: item.ltg_det_villaments_pmts_area_dts,
          address: item.ltg_det_villaments_address,
          RowID: item.ltg_det_mstRowID,
          type: item.ltg_type,
          featured: item.ltg_mark_as_featured,
        };
      default:
        return {
          description: item.ltg_det_desc,
          price: formatPrice(item.ltg_det_sale_price, true),
          suffixPrice: formatPrice(item.ltg_det_suffix_price, true),
          callForPrice: item?.ltg_det_call_for_price,
          bedroom: item.ltg_det_pmts_bed_rom,
          bathroom: item.ltg_det_pmts_bth_rom,
          parking: item.ltg_det_pmts_car_park,
          area: item.ltg_det_pmts_area_dts,
          address: item.ltg_det_address,
          RowID: item.ltg_det_mstRowID,
          type: item.ltg_type,
          featured: item.ltg_mark_as_featured,
        };
    }
  };

  const Featured = properties
    .filter(item =>
      item.ltg_mark_as_featured === "1" &&
      item.ltg_det_sold_type !== "1" &&
      item.ltg_det_sold_type !== 1
      )   
    .map((item) => {
      const mainImage = Array.isArray(item.attachments)
        ? item.attachments?.filter(att => att.type === "Main")
        : [];

      const imgUrl = mainImage.length > 0
        ? httpCommon.defaults.baseURL + mainImage[0].attachment
        : httpCommon.defaults.baseURL + '\\images\\defaultasset.jpg';

      // Get the property details based on the type
      const details = getPropertyDetails(item.ltg_type, item);

      return {
        key: item.ltg_det_mstRowID,
        title: item.ltg_title,
        propertyUrl: item.propertyUrl,
        imgUrl: imgUrl,
        ...details,
      };
    });

  useEffect(() => {
    getPropertiesData();
  }, []);

  const featuredCount = Featured.length;

  return (
    <>
      {/* <section className="relative bg-white featured-listings padingm" >
        <div className="max-w-4xl mx-auto">
          <Lottie
            animationData={cloufLottie}
            loop={true}
           
            className="absolute customAnimation top-0 left-0 right-0"
          />
        </div>
      </section> */}

      <section className="feature-collection bg-white py-8 md:py-10">
        <Container className="max-w-6xl space-y-5 px-4 md:px-6">
          <div className="max-w-xl">
            <h2 className="text-[#0F172A]">Featured Collection in Top Cities</h2>
            <p className="mt-1.5 text-[15px] text-[#727272]">
              Curated &amp; Handpicked Properties
            </p>
            <hr className="mt-3 h-1 w-24 rounded-full bg-[#FECE51]" />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 md:gap-6">
            {["Bengaluru", "Hyderabad"].map((region) => {
              const imageSrcMap = {
                Bengaluru: "/BeFunky-collage-2023-10-19T142541.707.jpg",
                Hyderabad: "/mumbai_tall_buildings.jpg",
                Tirupati:
                  "/FeatureImage_Top-10-Cheapest-Cities-In-India-20231-732x375.jpg",
              };
              const capitalizedRegion =
                region.charAt(0).toUpperCase() + region.slice(1);
              return (
                <Link
                  key={region}
                  to={`/Cities/${capitalizedRegion}`}
                  className="group relative block overflow-hidden rounded-2xl bg-slate-200 shadow-sm ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <div className="aspect-[16/10] w-full overflow-hidden sm:aspect-[5/3]">
                    <img
                      src={imageSrcMap[region]}
                      alt={capitalizedRegion}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 flex justify-center p-4 md:p-5">
                    <span className="rounded-md bg-white/95 px-5 py-2 text-center text-sm font-semibold text-[#0F172A] shadow-sm ring-1 ring-black/5">
                      {capitalizedRegion}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </Container>
      </section>

      <motion.section
        className="featured-listings relative overflow-hidden border-t border-gray-100 bg-gradient-to-b from-[#f0f4f8] via-[#f7f7f8] to-white py-10 md:py-14"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.4]"
          aria-hidden
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(203 213 225) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
        <Container className="relative z-[1] max-w-6xl px-4 md:px-6">
          <div className="mb-8 grid gap-8 lg:mb-10 lg:grid-cols-[1fr_minmax(200px,260px)] lg:items-end lg:gap-10">
            <div className="text-center lg:text-left">
              <div className="mb-3 flex justify-center lg:justify-start">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#727272] shadow-sm backdrop-blur-sm">
                  <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                  Verified inventory
                </span>
              </div>
              <h2 className="text-[#0F172A]">Discover Our Featured Listings</h2>
              <p className="mx-auto mt-2 max-w-lg text-[13px] leading-relaxed text-[#727272] lg:mx-0 md:text-[14px]">
                A few properties you can buy with your eyes closed. Properties that
                are verified. Buy with confidence and 100% assurance.
              </p>
              <hr className="mx-auto mt-4 h-1 w-20 rounded-full bg-[#FECE51] lg:mx-0" />
            </div>
            <div className="mx-auto w-full max-w-xs rounded-2xl border border-gray-200/90 bg-white/90 p-4 text-center shadow-[0_8px_30px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.03] backdrop-blur-sm lg:mx-0 lg:text-left">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#727272]">
                Live count
              </p>
              <p className="mt-1 text-3xl font-bold tabular-nums text-[#0F172A]">
                {featuredCount}
              </p>
              <p className="mt-0.5 text-[12px] text-[#727272]">
                featured {featuredCount === 1 ? "listing" : "listings"} in rotation
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200/90 bg-white/95 p-3 shadow-[0_12px_40px_rgba(15,23,42,0.07)] ring-1 ring-black/[0.02] backdrop-blur-[2px] md:p-5 lg:p-6">
            {featuredCount === 0 ? (
              <div className="py-14 text-center md:py-16">
                <p className="text-[15px] font-medium text-[#0F172A]">
                  No featured listings yet
                </p>
                <p className="mx-auto mt-2 max-w-sm text-[13px] text-[#727272]">
                  Mark properties as featured in admin, or browse the full catalogue.
                </p>
                <Link
                  to="/Property"
                  className="mt-5 inline-flex rounded-lg bg-[#0F172A] px-5 py-2.5 text-[12px] font-bold uppercase tracking-wide text-white transition hover:bg-slate-800"
                >
                  Browse all properties
                </Link>
              </div>
            ) : (
              <MultiCrousel
                data={Featured}
                details={true}
                carouselVariant="listings"
              />
            )}
          </div>

          {featuredCount > 0 ? (
            <p className="mt-5 text-center">
              <Link
                to="/Property"
                className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#0F172A] underline-offset-4 transition hover:text-blue-700 hover:underline"
              >
                See complete catalogue
                <span aria-hidden className="text-base leading-none">
                  →
                </span>
              </Link>
            </p>
          ) : null}
        </Container>
      </motion.section>

      <motion.section
        className="featured-listings relative overflow-hidden bg-white py-10 md:py-14"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
      >
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-blue-50/80 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-[#FECE51]/15 blur-3xl"
          aria-hidden
        />
        <Container className="relative z-[1] max-w-6xl px-4 md:px-6">
          <div className="mb-6 flex flex-col gap-5 border-b border-gray-100 pb-6 sm:flex-row sm:items-center sm:justify-between md:mb-8">
            <div className="flex items-start gap-3 sm:gap-4">
              <div
                className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[#0F172A] ring-1 ring-slate-200/80"
                aria-hidden
              >
                <LayoutGrid className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#727272]">
                    Developments
                  </p>
                  {featuredCount > 0 ? (
                    <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold tabular-nums text-[#0F172A]">
                      {featuredCount} live
                    </span>
                  ) : null}
                </div>
                <h2 className="mt-1 text-left text-[19px] font-semibold text-[#0F172A] md:text-[21px]">
                  Featured Projects
                </h2>
                <p className="mt-1 max-w-md text-left text-[13px] text-[#727272] md:text-[14px]">
                  Handpicked premium projects in prime locations—same curated set,
                  project-style presentation.
                </p>
              </div>
            </div>
            <Link
              to="/Property"
              className="inline-flex items-center justify-center self-start rounded-lg border-2 border-blue-600 bg-white px-4 py-2.5 text-center text-[11px] font-bold uppercase tracking-wide text-blue-600 shadow-sm transition hover:bg-blue-600 hover:text-white sm:self-auto"
            >
              View all
            </Link>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-[#fafbfc] to-white p-3 shadow-inner md:p-5 lg:p-6">
            {featuredCount === 0 ? (
              <div className="py-12 text-center text-[13px] text-[#727272] md:py-14">
                Add featured listings to populate this showcase.
              </div>
            ) : (
              <MultiCrousel
                data={Featured}
                details={true}
                carouselVariant="projects"
              />
            )}
          </div>
        </Container>
      </motion.section>


      {/* <section className="my-10 top-cities">
        <Container className="space-y-3">
          <h2 className="text-xl font-semibold md:text-2xl">Top Cities In India</h2>
          <p className="text-sm md:text-base">Best places to live in India</p>
          <hr className="bg-[#FECE51] w-32 h-1" />

          <div className="grid items-center justify-center grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 py-7">
            <a href="/Cities/Bengaluru" className="place-link">
              <div className="h-auto p-3 bg-white border rounded-md shadow-md md:p-5">
                <img src="/bengaluru.png" alt="Bengaluru" className="object-cover w-full h-24 rounded-md md:h-32" />
                <h3 className="mt-2 text-sm text-center md:text-base">Bengaluru</h3>
                <p className="text-xs font-light text-center md:text-sm">{propertyCounts.Bengaluru} properties</p>
              </div>
            </a>

            <div className="h-auto p-3 bg-white border rounded-md shadow-md md:p-5">
              <img src="/delhi.png" alt="Delhi" className="object-cover w-full h-24 rounded-md md:h-32" />
              <h3 className="mt-2 text-sm text-center md:text-base">Delhi</h3>
              <p className="text-xs font-light text-center md:text-sm">0 properties</p>
            </div>

            <div className="h-auto p-3 bg-white border rounded-md shadow-md md:p-5">
              <img src="/pune.png" alt="Pune" className="object-cover w-full h-24 rounded-md md:h-32" />
              <h3 className="mt-2 text-sm text-center md:text-base">Pune</h3>
              <p className="text-xs font-light text-center md:text-sm">0 properties</p>
            </div>

            <a href="/Cities/Hyderabad" className="place-link">
              <div className="h-auto p-3 bg-white border rounded-md shadow-md md:p-5">
                <img src="/hyderabad.png" alt="Hyderabad" className="object-cover w-full h-24 rounded-md md:h-32" />
                <h3 className="mt-2 text-sm text-center md:text-base">Hyderabad</h3>
                 <p className="text-xs font-light text-center md:text-sm">{propertyCounts.Hyderabad} properties</p> 
              </div>
            </a>

            <a href="/Cities/Tirupati" className="place-link">
              <div className="h-auto p-3 bg-white border rounded-md shadow-md md:p-5">
                <img src="/tirupati.jpg" alt="Tirupati" className="object-cover w-full h-24 rounded-md md:h-32" />
                <h3 className="mt-2 text-sm text-center md:text-base">Tirupati</h3>
                <p className="text-xs font-light text-center md:text-sm">{propertyCounts.Tirupati} properties</p>
              </div>
            </a>

            <div className="h-auto p-3 bg-white border rounded-md shadow-md md:p-5">
              <img src="/ahmedabad.jpg" alt="Ahmedabad" className="object-cover w-full h-24 rounded-md md:h-32" />
              <h3 className="mt-2 text-sm text-center md:text-base">Ahmedabad</h3>
              <p className="text-xs font-light text-center md:text-sm">0 properties</p>
            </div>
          </div>
        </Container>
      </section> */}

       
      </>
    );
  }

  export default Hero;
